import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { secureStorage, isNative, inAppBrowser } from '../utils/capacitor';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* silent */ }
    if (isNative) {
      await secureStorage.remove('token');
      await secureStorage.remove('token_expiry');
    }
    localStorage.removeItem('token');
    localStorage.removeItem('token_expiry');
    setUser(null);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }

      if (isNative) {
        const stored = await secureStorage.get('token');
        if (stored) {
          const expiry = await secureStorage.get('token_expiry');
          if (expiry && Date.now() > Number(expiry)) {
            await logout();
            setLoading(false);
            return;
          }
          localStorage.setItem('token', stored);
        }
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('token_expiry');
      }
      setLoading(false);
    };

    initAuth();
  }, [logout]);

  const _persistToken = async (accessToken) => {
    const expiry = String(Date.now() + 24 * 60 * 60 * 1000);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('token_expiry', expiry);
    if (isNative) {
      await secureStorage.set('token', accessToken);
      await secureStorage.set('token_expiry', expiry);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    await _persistToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, extraFields = {}) => {
    const response = await api.post('/auth/register', { email, password, ...extraFields });
    const { access_token, user: userData } = response.data;
    await _persistToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    if (isNative) {
      inAppBrowser.open(authUrl);
    } else {
      window.location.href = authUrl;
    }
  };

  const value = {
    user,
    setUser,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    register,
    loginWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
